import { Drawer } from '@/components/ats';
import { FormBuilder } from '@/components/formBuilder';
import { Flex, Box, Icon, useDisclosure } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { selectUsers } from '@/store/users/selectors';
import { useSelector } from 'react-redux';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { useShowToast } from '@/hooks';
import { useGetOrgRolesQuery } from '@/store/query/roles';
import { SHARED_EMAIL_API_SERVICES } from '../api';
import { useMutation } from '@tanstack/react-query';
import { EmailSaved } from '@/assets/icons/nav-v2';
import { KText } from '@/ui-library/components';
import { RenderVerification } from './RenderVerification';
import { components } from 'react-select';
import { domainValueContext } from './SharedEmailCard';

const ERROR_MESSAGES = {
  blacklisted_email: {
    message:
      'This email address is restricted for system use only. Use a different one.',
  },
};

interface createUpdateDrawerProps {
  createUpdateEmailDrawerActions?: any;
  sharedEmailData?: any;
  domainDetails?: any;
  triggerRef?: string;
  refetch?: () => void;
}

export const CreateUpdateDrawer: React.FC<createUpdateDrawerProps> = ({
  createUpdateEmailDrawerActions,
  sharedEmailData,
  domainDetails,
  triggerRef,
  refetch = () => {},
}) => {
  const popoverActions = useDisclosure();
  const formRef = useRef(null);
  const users = useSelector(selectUsers);
  const toast = useShowToast();
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [enableCTA, setEnableCTA] = useState(false);
  const [domainData, setDomainData] = useState({});
  const [updatedSharedEmailData, setUpdatedSharedEmailData] = useState(null);
  const [emailExists, setEmailExists] = useState(false);

  const { data: { org_roles: rolesData = [] } = {} } = useGetOrgRolesQuery({});

  const usersOptions = users?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
  }));
  const emailOptions = sharedEmailData?.domains?.map(
    (domain) =>
      ({
        label: domain?.domain,
        value: domain?.id,
        emails: sharedEmailData?.shared_emails?.map((email) => email?.email),
      } || [])
  );

  const rolesOptions = rolesData?.map((role) => ({
    ...role,
    label: role?.name,
    value: role?.id,
  }));

  const createNewEmail = useMutation({
    mutationFn: SHARED_EMAIL_API_SERVICES.createSharedEmail,
  });

  const updateEmail = useMutation({
    mutationFn: (params: { id: any; payload: any }) =>
      SHARED_EMAIL_API_SERVICES.updateSharedEmail(params.id, params.payload),
  });

  const getDomainInitialValues = () => {
    if (domainDetails) {
      return {
        email: domainDetails?.email,
        name: domainDetails?.name,
        owner_type: domainDetails?.owner_type || 'all_users',
        is_default: domainDetails?.is_default,
        owner_ids:
          domainDetails?.owner_type === 'users'
            ? domainDetails?.owners?.map((user) => user.id)
            : null,
        role_ids:
          domainDetails?.owner_type === 'roles'
            ? domainDetails?.owners?.map((role) => role.id)
            : null,
      };
    }
    return {};
  };

  const handleObservFunc = (values) => {
    // If it's a primary email, only validate the name field
    if (domainDetails?.is_primary) {
      if (values?.name) {
        setEnableCTA(true);
      } else {
        setEnableCTA(false);
      }
      return;
    }

    const hasBasicFields = values?.name && values?.email;

    const isValidShareWithAll = values?.owner_type === 'all_users';
    const isValidShareWithUsers =
      values?.owner_type === 'users' && values?.owner_ids?.length > 0;
    const isValidShareWithRoles =
      values?.owner_type === 'roles' && values?.role_ids?.length > 0;
    const isFormValid =
      hasBasicFields &&
      (isValidShareWithAll || isValidShareWithUsers || isValidShareWithRoles);

    if (isFormValid) {
      setEnableCTA(true);
    } else {
      setEnableCTA(false);
    }
  };
  const drawerContent = () => {
    const isPrimaryEmail = domainDetails?.is_primary;
    const addEmailConfig = [
      {
        name: 'email',
        label: 'Email address',
        type: 'selectWithCreateV1',
        isAutoComplete: true,
        createPlaceHolder: '',
        isRequired: true,
        disabled: domainDetails?.id,
        options: emailOptions,
        lowercase: true,
        isMulti: false,
        placeholder: 'Enter the email address',
        showNotes: true,
        noteCondition: domainDetails?.id
          ? null
          : {
              type: 'index',
              conditions: [
                {
                  type: 'custom',
                  field: 'email',
                  customFunc: (value) => {
                    const emailExists = sharedEmailData?.shared_emails?.some(
                      (email) => email.email === value
                    );
                    if (emailExists) {
                      setEmailExists(true);
                    } else {
                      setEmailExists(false);
                    }
                    return emailExists;
                  },
                },
                {
                  type: 'custom',
                  field: 'email',
                  customFunc: (value) => {
                    const domainValue = value?.split('@')[1] || '';
                    const domains = sharedEmailData?.domains?.map((domain) => ({
                      label: domain?.domain,
                      is_verified: domain?.is_verified,
                      is_primary: domain?.is_primary,
                    }));
                    const matchingOption = domains?.find((option) =>
                      option?.label?.includes(domainValue)
                    );
                    return matchingOption
                      ? !(
                          matchingOption.is_verified ||
                          matchingOption.is_primary
                        )
                      : true;
                  },
                },
                { type: 'skip' },
              ],
            },
        noteValues: domainDetails?.id
          ? []
          : [
              `This email address is already in use within your account. Duplicate shared emails are not allowed. Please use a different address or check your existing shared emails.`,
              `To use this email, you must first validate the associated domain. Follow the instructions that appear after adding the email to complete the validation.\n\n  Double-check your email for errors as you can't edit it after it is created.`,
              `Double-check your email for errors as you can't edit it after it is created.`,
            ],
        customNoOptionsMessage: (props) => {
          const searchTerm = props?.selectProps?.inputValue;
          const searchTermLen = searchTerm.length;
          return (
            <components.NoOptionsMessage {...props}>
              <Box
                fontSize="14px"
                borderColor="border-color"
                color="secondary-700"
                textAlign="left"
              >
                {searchTermLen < 2
                  ? 'Please type 2 or more letters'
                  : 'Enter valid email to add as new option'}
              </Box>
            </components.NoOptionsMessage>
          );
        },
        validateNewOption: (email) => {
          // Validate email format
          const emailRegex =
            /^[a-zA-Z0-9._%+-]+(?:\.[a-zA-Z0-9._%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;
          if (!emailRegex.test(email)) {
            return false;
          }

          // Check if email already exists in shared_emails
          const emailExists = (sharedEmailData?.shared_emails || []).some(
            (item) => item.email.toLowerCase() === email.toLowerCase()
          );

          if (emailExists) {
            toast({
              title: 'Email already exists',
              status: 'error',
              description: 'This email has already been added',
            });
            return false;
          }

          return true;
        },
        styles: { marginBottom: '60px' },
      },
      {
        name: 'name',
        label: 'Display name',
        type: 'input',
        isRequired: true,
        maxLength: 60,
        placeholder: 'Enter the display name',
      },
      {
        name: 'owner_type',
        label: 'Share with',
        type: 'select',
        isSearchable: false,
        isRequired: true,
        disabled: isPrimaryEmail,
        options: [
          {
            label: 'Individual users',
            value: 'users',
            desc: 'Select specific users who can use this email address for sending emails ',
          },
          {
            label: 'Roles',
            value: 'roles',
            desc: 'Choose roles that can use this email address for sending emails. All current and future users in these roles will have access.',
          },
          {
            label: 'All users',
            value: 'all_users',
            desc: 'Everyone in Kula will be able to use this email address for sending emails',
          },
        ],
        components: {
          Option: SelectWithDescriptionOption,
        },
      },
      {
        isConditional: true,
        conditionConfig: { field: 'owner_type', value: 'users' },
        name: 'owner_ids',
        label: 'Users',
        type: 'select',
        options: usersOptions,
        errorMessage: 'Users are required',
        components: {
          Option: SelectWithDescriptionOption,
        },
        customFormatLabel: SelectOptionWithAvatar,
        descKey: 'email',
        avatarKey: 'avatar',
        isNearLabel: true,
        isRequired: true,
        isMulti: true,
        disabled: isPrimaryEmail,
        width: '100%',
        placeholder: 'Search users',
        showAvatar: true,
        styles: { gap: '60px' },
      },
      {
        isConditional: true,
        conditionConfig: { field: 'owner_type', value: 'roles' },
        name: 'role_ids',
        label: 'Roles',
        type: 'select',
        errorMessage: 'Roles are required',
        options: rolesOptions,
        isRequired: true,
        isMulti: true,
        disabled: isPrimaryEmail,
        width: '100%',
        placeholder: 'Select roles',
        styles: { gap: '60px' },
      },
    ];
    if (domainDetails && domainDetails?.is_verified) {
      addEmailConfig.push({
        name: 'is_default',
        label: 'Make default',
        type: 'checkbox',
        isRequired: false,
        //@ts-ignore
        defaultChecked: domainDetails?.is_default,
        isEditable: domainDetails?.is_verified,
        disabled: domainDetails?.is_default,
      });
    }
    return !showSuccessPage ? (
      <Box px="1.25rem" py=".5em">
        <FormBuilder
          config={addEmailConfig}
          formRef={formRef}
          initialValues={getDomainInitialValues()}
          isObserveEnabled={true}
          observFunc={handleObservFunc}
        />
      </Box>
    ) : (
      renderSuccessPage()
    );
  };

  const submitFormsAndGetErrors = async () => {
    if (formRef?.current) {
      await formRef?.current?.submitForm();
      return !formRef?.current?.isValid;
    }
  };

  const handleDrawerSubmit = async () => {
    const errors = await submitFormsAndGetErrors();
    if (errors && !domainDetails) {
      return;
    } else {
      const cleanObject = (obj) => {
        return Object.fromEntries(
          Object.entries(obj).filter(([_, value]) => {
            if (value === null || value === undefined) return false;
            if (Array.isArray(value) && value.length === 0) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            return true;
          })
        );
      };
      const values = cleanObject(formRef?.current?.values);
      if ('role_ids' in values && values?.owner_type === 'roles') {
        values.owner_ids = values.role_ids;
        delete values.role_ids;
      } else if (values?.owner_type === 'all_users') {
        delete values.role_ids;
        delete values.owner_ids;
        delete values.owner_type;
      }

      const wrappedData = {
        shared_email: values,
      };
      if (domainDetails) {
        updateEmail.mutate(
          {
            id: domainDetails?.id,
            payload: wrappedData,
          },
          {
            onSuccess: (res) => {
              toast({ title: 'Shared email updated successfully' });
              createUpdateEmailDrawerActions?.onClose();
              refetch();
            },
          }
        );

        createUpdateEmailDrawerActions?.onClose();
      } else {
        createNewEmail.mutate(wrappedData, {
          onSuccess: (res) => {
            const { data } = res || {};
            const { domain } = data || {};
            const isDomainVerified = domain?.is_verified;
            const isDomainPrimary = domain?.is_primary;
            if (data) {
              if (!isDomainVerified && !isDomainPrimary) {
                refetch();
                setShowSuccessPage(true);
              } else {
                refetch();
                createUpdateEmailDrawerActions?.onClose();
              }
              toast({ title: 'Shared email added successfully' });
            } else {
              if (
                //@ts-ignore
                res?.errors?.[0] === 'Mailgun domain creation failed'
              ) {
                const domain =
                  typeof wrappedData?.shared_email?.email === 'string'
                    ? wrappedData.shared_email.email.split('@')[1]
                    : undefined;
                toast({
                  title: (
                    <span>
                      Unable to add this email as this domain is already
                      registered with our domain provider, Mailgun. Try using a
                      subdomain like @talent.{domain} or{' '}
                      <a
                        href="https://support.kula.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'white' }}
                      >
                        contact support
                      </a>{' '}
                      for help.
                    </span>
                  ),
                  status: 'error',
                  isClosable: false,
                });
              } else {
                // Check for specific error types
                //@ts-ignore
                const errorMessage = res?.errors?.[0];
                const errorConfig = ERROR_MESSAGES[errorMessage] || {
                  message: 'Failed to add the shared email',
                };

                toast({
                  title: errorConfig.message,
                  status: 'error',
                  //@ts-ignore
                  // description: res?.errors?.[0],
                });
              }
              createUpdateEmailDrawerActions?.onClose();
            }
            // Store the domain response and send it to RenderVerification
            if (domain) {
              setDomainData(domain);
              // Store the data to pass to the RenderVerification component
              const updatedSharedEmailData = {
                ...sharedEmailData,
                shared_emails: [
                  ...(sharedEmailData?.shared_emails || []),
                  data?.shared_email,
                ],
              };

              // Set the updated shared email data state
              setUpdatedSharedEmailData(updatedSharedEmailData);
            }
          },
        });
      }
    }
  };

  const renderSuccessPage = () => {
    // Create a contextValue object that combines the domain data and shared email data
    const contextValue = {
      domainDetails: domainData ? domainData : domainDetails,
      sharedEmailData: updatedSharedEmailData || sharedEmailData,
    };

    return (
      <Flex
        h="100%"
        alignItems="center"
        justifyContent="center"
        flexDir="column"
        px="100px"
        gap="6px"
      >
        <Icon as={EmailSaved} />
        <KText size="xs-semi-bold">EMAIL ADDRESS SAVED</KText>
        <KText size="md-medium" color="secondary-900">
          To start using the email, validate the associated domain by adding
          entries to your DNS records. Your IT department can assist with this
          process.
        </KText>
        <domainValueContext.Provider value={contextValue}>
          <RenderVerification
            popoverActions={popoverActions}
            triggerRef="createEmailDrawer"
            createUpdateEmailDrawerActions={createUpdateEmailDrawerActions}
            refetch={refetch}
            domainDetails={domainData ? domainData : domainDetails}
            sharedEmailData={updatedSharedEmailData || sharedEmailData}
          />
        </domainValueContext.Provider>
      </Flex>
    );
  };

  return (
    <>
      <Drawer
        drawerActions={{
          isOpen: createUpdateEmailDrawerActions?.isOpen,
          onClose: createUpdateEmailDrawerActions?.onClose,
        }}
        headerText={domainDetails?.id ? 'Edit email' : 'Add email'}
        ctaLabel={domainDetails?.id ? 'Update' : 'Save'}
        headerTextProps={{
          borderBottom: '1px solid',
          borderBottomColor: 'border-color',
        }}
        showFooter={!showSuccessPage}
        showHeader={!showSuccessPage}
        hasFooterDivider={true}
        isCtaLoading={createNewEmail?.isPending || updateEmail?.isPending}
        onDrawerSubmit={handleDrawerSubmit}
        ctaProps={{
          isDisabled: !enableCTA || emailExists,
          loadingText: 'Creating',
        }}
      >
        {drawerContent()}
      </Drawer>
    </>
  );
};
